import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Columns,
  Column,
  Stack,
  PageBlock,
  ContentBlock,
  Divider,
} from 'braid-design-system';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';

import {
  PERSONAL_DETAILS,
  SUMMARY,
  CAREER_HISTORY,
  RESUME,
  NEXT_ROLE,
  SKILLS,
  PROFILE_VISIBILITY,
  EDUCATION,
  TIPS,
  LICENCES,
  LANGUAGES,
  PROFILE_VISIBILITY_V2,
  SHAREABLE_PROFILE,
  PROFILE_INSIGHTS,
  SUGGESTIONS,
  VERIFICATIONS,
} from 'src/components/Router';
import { useAppConfig } from 'src/modules/appConfig';
import { isFeatureActive } from 'src/modules/features';
import { useIsShareableProfileFeatureActive } from 'src/modules/features/shareableProfileAbTest';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import { hotjarEvent } from 'src/modules/hotjar';

import translations from './.vocab';
import { DefaultResumeNudge } from './DefaultResumeNudge/DefaultResumeNudge';
import { Loading } from './Loading/Loading';
import { usePreferredLocations } from './NextRole/PreferredLocation/data';
import { useRightsToWork } from './NextRole/RightToWork/data';
import { usePersonalDetails } from './PersonalDetails/data';
import { useGetProfileInsights } from './ProfileInsights/data';
import { useResumes } from './Resume/data';
import { TopNudge } from './TopNudge/TopNudge';
import { useVerifications } from './Verifications/data';
import { fetchMyProfile, myProfileRendered } from './actionCreators';
import { isPreferredLocationDeprecated, isRTWCoarse } from './utils';

import * as styles from './Profile.css';

const sectionSpace = 'xxlarge';
const rightColumnSectionSpace = 'large';

export const Profile = React.memo(() => {
  const { t } = useTranslations(translations);

  const { showFeature, brand, isUnifiedSite } = useAppConfig();
  const { resumes, defaultResumeId } = useResumes();
  const { personalDetails } = usePersonalDetails();
  const { preferredLocations } = usePreferredLocations();
  const { rightsToWork } = useRightsToWork();
  const { showVerifications } = useVerifications();

  const storeProps = useTypedSelector(
    ({ profile: { isLoading } }) => ({
      isLoading,
    }),
    (prevState, nextState) =>
      JSON.stringify(prevState) === JSON.stringify(nextState),
  );
  const dispatch = useDispatch();

  const { isLoading } = storeProps;
  const { showProfileInsights } = useGetProfileInsights();

  useEffect(() => {
    if (showProfileInsights) hotjarEvent('profile-insights-test-group-show');
  }, [showProfileInsights]);

  useEffect(() => {
    if (isLoading) {
      dispatch(fetchMyProfile());
    } else {
      dispatch(myProfileRendered());
    }
  }, [dispatch, isLoading]);

  const { isShareableProfileFeatureActive } =
    useIsShareableProfileFeatureActive();

  const showProfileSuggestions = false;

  // We need feature flag for profile-strengths to pass cypress tests for now.
  const showTips =
    isFeatureActive('profile-strengths') || !showProfileSuggestions;

  const rightColumnContents = (
    <>
      <Stack space={rightColumnSectionSpace}>
        {isShareableProfileFeatureActive ? (
          <>
            <Loading card={PROFILE_VISIBILITY_V2} />
            {/* SHAREABLE_PROFILE will only available for legacy flow - JobsDB legacy */}
            {isUnifiedSite ? null : <Loading card={SHAREABLE_PROFILE} />}
            {showProfileInsights ? (
              <>
                <Divider />
                <Loading card={PROFILE_INSIGHTS} />
              </>
            ) : null}
            {showVerifications ? (
              <>
                <Divider />
                <Loading card={VERIFICATIONS} />
              </>
            ) : null}
          </>
        ) : (
          <Loading card={PROFILE_VISIBILITY} />
        )}
      </Stack>
      {showProfileSuggestions ? (
        <>
          <Box paddingTop={rightColumnSectionSpace} paddingBottom="gutter">
            <Divider />
          </Box>
          <Loading card={SUGGESTIONS} />
        </>
      ) : null}
      {showTips ? (
        <>
          <Box paddingTop={rightColumnSectionSpace} paddingBottom="xxlarge">
            <Divider />
          </Box>
          <Loading card={TIPS} />
        </>
      ) : null}
    </>
  );

  const profileUrl = useMelwaysLink({ path: '/profile', absolute: true });

  const showPriorityNudge = () => {
    if (personalDetails?.currentLocation2?.isDeprecated)
      return <TopNudge nudgeType="HOME_LOCATION" />;
    else if (resumes.length > 0 && defaultResumeId === '')
      return <DefaultResumeNudge />;
    else if (
      rightsToWork &&
      rightsToWork.length > 0 &&
      isRTWCoarse(rightsToWork)
    )
      return <TopNudge nudgeType="RTW" />;
    else if (
      preferredLocations &&
      preferredLocations.length > 0 &&
      isPreferredLocationDeprecated(preferredLocations)
    )
      return <TopNudge nudgeType="PREFERRED_LOCATIONS" />;
    return null;
  };

  return (
    <>
      <Helmet>
        <title>{t('My Profile | brand', { brand })}</title>
        <link rel="canonical" href={profileUrl} />
      </Helmet>
      <Box paddingX={{ tablet: 'gutter' }}>
        <Stack space={sectionSpace}>
          <ContentBlock width="large">
            <Box
              background="brand"
              borderRadius={{ tablet: 'xlarge' }}
              paddingY="xlarge"
              className={styles.banner}
            >
              <PageBlock width="medium">
                <Loading card={PERSONAL_DETAILS} />
              </PageBlock>
            </Box>
          </ContentBlock>

          <PageBlock width="medium">
            <Columns space={sectionSpace} collapseBelow="desktop" reverse>
              <Column width="content">
                <Box className={styles.rightColumn}>{rightColumnContents}</Box>
              </Column>
              <Column>
                <Stack space={sectionSpace}>
                  {showPriorityNudge()}
                  {showFeature('personal-summary') && (
                    <Loading card={SUMMARY} />
                  )}
                  <Loading card={CAREER_HISTORY} />
                  <Loading card={EDUCATION} />
                  <Loading card={LICENCES} />
                  <Loading card={SKILLS} />
                  <Loading card={LANGUAGES} />
                  <Loading card={RESUME} />
                  <Loading card={NEXT_ROLE} />
                </Stack>
              </Column>
            </Columns>
          </PageBlock>
        </Stack>
      </Box>
    </>
  );
});
